import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Phone, PhoneOff, Mic, Video } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import Avatar from '../Avatar';
import { useAuth } from '../../context/AuthContext';
import { getMyRing, acceptCall, rejectCall } from '../../api/o2oCalls';
import { primeAudio, startRingtone } from '../../lib/sounds';

/**
 * Phase 54 · Global incoming-call listener.
 * Polls /api/o2o/ring every 4s (paused while the tab is hidden) for any
 * RINGING call that targets ME:
 *   • creator receiving a direct call
 *   • waitlisted user being auto-called when the creator frees up
 */
const IncomingCallListener = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [ring, setRing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [remaining, setRemaining] = useState(0);
  const dismissed = useRef(null);

  const onCallPage = location.pathname.startsWith('/call/');

  // Unlock the AudioContext on the first gesture so the ringtone can play later.
  useEffect(() => (user ? primeAudio() : undefined), [user]);

  // Ringtone + vibration while the incoming-call dialog is open.
  useEffect(() => {
    if (!ring || onCallPage) return undefined;
    console.info('[o2o] ringtone start', ring.id);
    const stop = startRingtone();
    return () => { stop(); console.info('[o2o] ringtone stop'); };
  }, [ring?.id, onCallPage]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user || onCallPage) return undefined;
    let alive = true;
    const poll = async () => {
      if (document.hidden) return; // pause while tab is in background
      try {
        const d = await getMyRing();
        if (!alive) return;
        const r = d.ring;
        if (r && r.id !== dismissed.current) {
          setRing(r);
          setRemaining(Math.floor(r.ring_remaining_seconds || 0));
        } else if (!r) {
          setRing(null);
        }
      } catch (_e) { /* silent */ }
    };
    poll();
    const t = setInterval(poll, 4000);
    const onVisible = () => { if (!document.hidden) poll(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { alive = false; clearInterval(t); document.removeEventListener('visibilitychange', onVisible); };
  }, [user, onCallPage]);

  // Countdown while ringing.
  useEffect(() => {
    if (!ring) return undefined;
    const t = setInterval(() => {
      setRemaining((s) => {
        if (s <= 1) { setRing(null); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(t);
  }, [ring]);

  if (!user || !ring || onCallPage) return null;

  const iAmCreator = ring.i_am === 'creator';
  const other = iAmCreator ? ring.caller : ring.creator;

  const onAccept = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await acceptCall(ring.id);
      const id = ring.id;
      setRing(null);
      navigate(`/call/${id}`);
    } catch (_e) {
      setRing(null);
    } finally {
      setBusy(false);
    }
  };

  const onReject = async () => {
    if (busy) return;
    setBusy(true);
    dismissed.current = ring.id;
    try { await rejectCall(ring.id); } catch (_e) { /* ignore */ }
    setRing(null);
    setBusy(false);
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onReject(); }}>
      <DialogContent className="max-w-sm text-center" data-testid="incoming-call-dialog">
        <DialogHeader className="items-center">
          <div className="relative mx-auto mb-2">
            <span className="absolute inset-0 animate-ping rounded-full bg-emerald-500/30" />
            <Avatar src={other?.avatar_url} alt={other?.username} size={72} />
          </div>
          <DialogTitle data-testid="incoming-call-title">
            {iAmCreator
              ? `@${other?.username} ti sta chiamando`
              : `È il tuo turno! @${other?.username} è disponibile`}
          </DialogTitle>
          <DialogDescription className="flex items-center justify-center gap-2">
            {ring.mode === 'video' ? <Video className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
            {ring.mode === 'video' ? 'Videochiamata' : 'Chiamata audio'} · {ring.rate_credits_per_min} crediti/min
            <span className="tabular-nums text-red-500">({remaining}s)</span>
          </DialogDescription>
        </DialogHeader>
        <div className="flex justify-center gap-4 py-2">
          <Button
            onClick={onReject}
            disabled={busy}
            className="h-12 w-32 rounded-full bg-red-600 text-white hover:bg-red-700"
            data-testid="incoming-call-reject-btn"
          >
            <PhoneOff className="mr-2 h-4 w-4" /> Rifiuta
          </Button>
          <Button
            onClick={onAccept}
            disabled={busy}
            className="h-12 w-32 rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
            data-testid="incoming-call-accept-btn"
          >
            <Phone className="mr-2 h-4 w-4" /> Accetta
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default IncomingCallListener;
